import { motion } from 'motion/react';

interface KaryvaLogoAnimated3DProps {
  font?: 'inter' | 'manrope' | 'jakarta' | 'sora' | 'work' | 'space' | 'outfit' | 'dm';
  size?: 'compact' | 'header' | 'large' | 'hero';
  darkBg?: boolean;
  autoPlay?: boolean;
  triangleScale?: number;
}

export function KaryvaLogoAnimated3D({ 
  font = 'inter',
  size = 'header', 
  darkBg = false,
  autoPlay = true,
  triangleScale = 0.95
}: KaryvaLogoAnimated3DProps) {
  const dimensions = {
    compact: {
      fontSize: 18,
      letterSpacing: '0.08em'
    },
    header: {
      fontSize: 28,
      letterSpacing: '0.08em'
    },
    large: {
      fontSize: 42,
      letterSpacing: '0.08em'
    },
    hero: {
      fontSize: 68,
      letterSpacing: '0.08em'
    }
  };

  // Safety check: ensure size is valid, fallback to 'header' if not
  const validSize = (size && dimensions[size]) ? size : 'header';
  const config = dimensions[validSize];
  const primaryColor = darkBg ? '#ffffff' : '#0a0a0a';

  const fontFamilies = {
    inter: '"Inter", system-ui, sans-serif',
    manrope: '"Manrope", system-ui, sans-serif',
    jakarta: '"Plus Jakarta Sans", system-ui, sans-serif',
    sora: '"Sora", system-ui, sans-serif',
    work: '"Work Sans", system-ui, sans-serif',
    space: '"Space Grotesk", system-ui, sans-serif',
    outfit: '"Outfit", system-ui, sans-serif',
    dm: '"DM Sans", system-ui, sans-serif'
  };

  const triangleSize = config.fontSize * triangleScale;

  return (
    <div 
      style={{
        color: primaryColor,
        fontSize: `${config.fontSize}px`,
        fontWeight: 500,
        letterSpacing: config.letterSpacing,
        fontFamily: fontFamilies[font],
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: `${config.fontSize * 0.08}px`
      }}
    >
      <span>K</span>
      <span>A</span>
      <span>R</span>
      <span>Y</span>
      <span>V</span>
      <span style={{ 
        display: 'inline-flex',
        alignItems: 'baseline',
        height: `${config.fontSize}px`,
        perspective: '1000px',
        marginLeft: `${-config.fontSize * 0.15}px`,
        position: 'relative',
        top: `${config.fontSize * 0.15}px`
      }}>
        <svg width={triangleSize} height={triangleSize} viewBox="0 0 100 100" style={{ display: 'block' }}>
          <defs>
            {/* Glow filter for depth */}
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Outer static triangle outline - equal stroke width on all sides */}
          <path 
            d="M 50 15 L 85 85 L 15 85 Z" 
            stroke={primaryColor} 
            strokeWidth="2.5" 
            fill="none" 
            strokeLinejoin="miter"
            strokeLinecap="square"
          />
          
          {/* Spinning inner triangle - 3D faceted design */}
          <motion.g
            style={{ 
              transformOrigin: '50% 58%',
              transformStyle: 'preserve-3d'
            }}
            initial={{ rotateY: 0 }}
            animate={autoPlay ? { 
              rotateY: [0, 360],
            } : {}}
            transition={{
              duration: 3,
              ease: "linear",
              repeat: Infinity
            }}
          >
            {/* Base goldenrod triangle */}
            <motion.path
              d="M 50 28 L 75 78 L 25 78 Z"
              fill="#DAA520"
              filter="url(#glow)"
            />
            
            {/* Right bevel edge - bright gold highlight */}
            <path
              d="M 50 28 L 53 32 L 72 75 L 75 78 Z"
              fill="#FFD700"
            />
            
            {/* Left bevel edge - dark shadow */}
            <path
              d="M 50 28 L 47 32 L 28 75 L 25 78 Z"
              fill="#B8860B"
            />
            
            {/* Bottom bevel edge - medium shadow */}
            <path
              d="M 25 78 L 28 75 L 72 75 L 75 78 Z"
              fill="#C19A1F"
            />
            
            {/* Top bevel cap - bright highlight */}
            <path
              d="M 50 28 L 47 32 L 53 32 Z"
              fill="#FFD700"
            />
          </motion.g>
        </svg>
      </span>
    </div>
  );
}